import type { ContextualData, JourneyEventContextualData } from '../contextualData';
import { emptyContextualData } from '../contextualData';
import type { EventTrackingAction, SentryEvent, SentryOptions, UserEvent } from '../declarations';
import type { JourneyEventWrapper } from '../declarations/JourneyEvent';
import { globalObservability } from '../global';

export function buildDefaultSentryOpts(
  // transform these to opts
  opts: {
    env: string;
    appName: string;
    version: string;
  },
): SentryOptions {
  const { env, appName, version } = opts;
  return {
    environment: env,
    release: `${appName}@${version}`,
    ignoreErrors: [...DEFAULT_IGNORE_ERRORS],
    beforeSend,
  };
}

export function trackEventInSentry<AppActions>(
  action: EventTrackingAction<AppActions>,
  label?: string,
  contextualData: ContextualData = emptyContextualData,
) {
  // TODO: Map actions to different breadcrumb types? https://develop.sentry.dev/sdk/event-payloads/breadcrumbs/#breadcrumb-types
  globalObservability.getSentryClient().addBreadcrumb?.({
    category: action,
    message: label,
    data: {
      ...contextualData.data,
      adClickContext: contextualData.adClickContext,
      campaignContext: contextualData.campaignContext,
      customerContext: contextualData.customerContext,
      exclusiveOffersContext: contextualData.exclusiveOffersContext,
      leadContext: contextualData.leadContext,
      sellDirectContext: contextualData.sellDirectContext,
    },
    timestamp: Date.now(),
  });
}

export function trackUserEventInSentry(
  event: UserEvent,
  contextualData: ContextualData = emptyContextualData,
) {
  globalObservability.getSentryClient().addBreadcrumb?.({
    category: Object.keys(event)[0] ?? 'unknownUserEvent',
    data: {
      ...event,
      ...contextualData.data,
      adClickContext: contextualData.adClickContext,
      campaignContext: contextualData.campaignContext,
      customerContext: contextualData.customerContext,
      exclusiveOffersContext: contextualData.exclusiveOffersContext,
      leadContext: contextualData.leadContext,
      sellDirectContext: contextualData.sellDirectContext,
    },
    timestamp: Date.now(),
  });
}

export function trackJourneyEventInSentry(
  event: Partial<JourneyEventWrapper>,
  contextualData: JourneyEventContextualData,
) {
  const category = Object.keys(event)[0] ?? 'unknownUserEvent';
  globalObservability.getSentryClient().addBreadcrumb?.({
    category,
    data: {
      ...event,
      campaignContext: contextualData.campaignContext,
    },
    timestamp: Date.now(),
  });
}

export function trackErrorInSentry(
  label: string,
  error?: Error,
  contextualData: ContextualData = emptyContextualData,
): ContextualData {
  let newContextualData: ContextualData = emptyContextualData;
  globalObservability.getSentryClient().withScope?.((scope) => {
    scope.setFingerprint([label]);
    scope.setUser(
      contextualData.user
        ? {
            ...contextualData.user,
            id: contextualData.user.humanID,
          }
        : null,
    );

    scope.setTags({
      ...contextualDataToTags(contextualData.data),
      label, // add label as a tag so we can search for it
    });
    scope.setExtras(contextualData.data);

    const eventId = globalObservability
      .getSentryClient()
      .captureException?.(error || new Error(label));
    newContextualData = {
      data: {
        'sentry-event-id': eventId,
      },
    };
  });
  return newContextualData;
}

/**
 * Sentry tags are powerful because they are indexed and can be searched in the Sentry app.
 * However, there are restrictions, as documented here: https://docs.sentry.io/platforms/javascript/enriching-events/tags/
 * This function converts all simple (i.e. non-object) data values into tags.
 */
function contextualDataToTags(data: { [k: string]: unknown }): { [k: string]: string } {
  let tags: { [k: string]: string } = {};
  Object.keys(data).forEach((key) => {
    const value = data[key];
    switch (typeof value) {
      case 'undefined':
      case 'boolean':
      case 'number':
      case 'bigint':
      case 'string':
      case 'symbol':
        tags[key] = String(value);
        break;
      case 'object':
        if (value === null) {
          tags[key] = 'null';
        }
        break;
    }
  });
  return tags;
}

// Also used in js/packages/cosmos/src/reporters/sentry.ts
// Consider keeping the two in sync.
// Please add a comment for each ignored error.
const DEFAULT_IGNORE_ERRORS: ReadonlyArray<string | RegExp> = [
  // Error generated by a bug in auto-fill library from Chrome Mobile on iOS
  // https://github.com/getsentry/sentry/issues/5267
  'Blocked a frame with origin',

  // error generated by microsoft outlook's safelink crawler on pages we email out
  // https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062
  'Object Not Found Matching Id',

  // Error that MAY indicate a performance issue due to browser implementation, but has been shown to be safe to ignore.
  // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
];

type Mapper = (arg: SentryEvent | null) => SentryEvent | null;

const beforeSendFn = (mappers: ReadonlyArray<Mapper>, event: SentryEvent) => {
  return mappers.reduce<SentryEvent | null>((acc, mapper) => mapper(acc), event);
};

const beforeSend = (event: SentryEvent | null): SentryEvent | null => {
  if (!event) {
    return null;
  }

  return beforeSendFn(MAPPERS, event);
};

const isPhantomJs: Mapper = (event) => {
  // Silences sentries for crawlers/prerender.io
  if (typeof window === 'undefined') {
    return event;
  }
  const isPhantomJS = /PhantomJS/.test(window.navigator.userAgent);
  return !isPhantomJS ? event : null;
};

// ignored because of this stackoverflow https://stackoverflow.com/questions/51744083/invalid-argument-google-map-error-in-edge-browser
const isInvalidArgumentError: Mapper = (event) => {
  const firstFrameFilename = event?.exception?.values?.[0]?.stacktrace?.frames?.[0]?.filename || '';
  const eventMessage = event?.exception?.values?.[0]?.value || '';

  if (eventMessage.includes('Invalid argument') && firstFrameFilename.includes('maps-api-v3')) {
    return null;
  }
  if (
    eventMessage.includes('Could not load "map"') ||
    eventMessage.includes('Could not load "marker"') ||
    // Error occurs when watching Vimeo videos on Safari
    eventMessage.includes('The operation was aborted.')
  ) {
    return null;
  }
  return event;
};

// Drops events that originate from a Cloudflare WAF block (Error 1005 ASN ban).
// The WAF returns an HTML page that Apollo wraps as a GraphQL error, surfacing
// in Sentry as a real GraphQL failure even though the blocked traffic is
// distributed bot/scraper activity, not customer-facing.
//
// Primary fingerprint: `<!doctype html>` AND `Code: 403)` in event.message.
// The combination is virtually impossible for a legitimate GraphQL error
// (which returns JSON), and it survives Apollo's tendency to truncate the
// embedded response body before the deeper WAF-specific strings appear.
//
// Secondary fingerprint (`errorCode: 1005` / `used Cloudflare to restrict
// access`) is retained as a safety net for the rare event whose `message`
// carries the full WAF HTML. The HTTP 403 status alone and the `cf-ray`
// header are intentionally NOT used — both appear on legitimate traffic
// (every Cloudflare-proxied response carries a ray ID), which would silently
// drop real authz failures.
//
// Originating investigation: Sentry issues RECEPTION-FE-2XPZ (getHuman) and
// RECEPTION-FE-2XPS (GetSellerInputQuery).
const isCloudflareWafBlock: Mapper = (event) => {
  if (!event) {
    return event;
  }

  const message = typeof event.message === 'string' ? event.message : '';
  const exceptionValue = event.exception?.values?.[0]?.value || '';

  const extras = event.extra ?? {};
  const extrasBlob = Object.values(extras)
    .map((v) => {
      if (typeof v === 'string') {
        return v;
      }
      if (v === null || v === undefined) {
        return '';
      }
      try {
        return JSON.stringify(v);
      } catch {
        return '';
      }
    })
    .join('\n');
  // Case-insensitive: `<!DOCTYPE html>` is a valid HTML5 doctype variant, and
  // Apollo's wrapper text could plausibly change capitalization across versions.
  const haystack = `${message}\n${exceptionValue}\n${extrasBlob}`.toLowerCase();

  const isHtmlBlocked403 = haystack.includes('<!doctype html>') && haystack.includes('code: 403)');
  const isCloudflareSpecific =
    haystack.includes('errorcode: 1005') || haystack.includes('used cloudflare to restrict access');

  return isHtmlBlocked403 || isCloudflareSpecific ? null : event;
};

const MAPPERS: ReadonlyArray<Mapper> = [isPhantomJs, isInvalidArgumentError, isCloudflareWafBlock];

export const test = {
  beforeSend,
  beforeSendFn,
  isCloudflareWafBlock,
  isInvalidArgumentError,
  isPhantomJs,
};
